import { useOnClickOutside } from 'common/utils/hooks/useOnClickOutside';
import { Flex } from 'component-library/components/layout/Flex';
import omit from 'lodash/omit';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { device } from 'shared/theme';
import styled from 'styled-components';

import { TextInput } from './TextInput';

const MONTH = 'month';
const DAY = 'day';
const YEAR = 'year';

type DateInputs = [typeof MONTH, typeof DAY, typeof YEAR];

const ShortInput = styled.label`
  margin-right: 12px;
`;

const LongInput = styled.label``;

const StyledTextInput = styled(TextInput)`
  ${`@media ${device.sm}`} {
    input {
      padding: 0 10px;
    }
  }
`;

interface DateInputProps {
  error?: boolean;
  onBlur?: (value?: string) => void;
  onChange: (value: string) => void;
  value?: string;
  className?: string;
}

interface DateValue {
  month: string | null;
  day: string | null;
  year: string | null;
}

export const DateInput = React.forwardRef(
  (props: DateInputProps, monthInputRef) => {
    const { onChange, onBlur, value } = props;
    const [dateValue, setDateValue] = useState<DateValue>({
      month: null,
      day: null,
      year: null,
    });
    const [dateElementsTouched, setDateElementTouched] = useState({
      month: false,
      day: false,
      year: false,
    });
    const [clickedOutside, setClickedOutside] = useState(false);
    const ref = useRef() as React.MutableRefObject<HTMLInputElement>;
    const dayInputRef = useRef() as React.RefObject<HTMLInputElement>;
    const yearInputRef = useRef() as React.RefObject<HTMLInputElement>;

    useEffect(() => {
      if (value) {
        const elements = value.split('-');
        setDateValue({
          year: elements[0],
          month: elements[1],
          day: elements[2],
        });
      }
    }, [value]);

    const handleChange = (value: string, element: DateInputs[number]) => {
      setDateValue({ ...dateValue, ...{ [element]: value } });

      if (value.length === 2) {
        if (element === MONTH) {
          dayInputRef.current?.focus();
        }

        if (element === DAY) {
          yearInputRef.current?.focus();
        }
      }
    };

    const triggerBlurAndChange = () => {
      onBlur && onBlur(value);
      const newValue =
        dateValue.month && dateValue.day && dateValue.year
          ? `${dateValue.year}-${dateValue.month}-${dateValue.day}`
          : '';
      onChange(newValue);
    };

    useOnClickOutside(ref, () => setClickedOutside(true));
    useEffect(() => {
      if (clickedOutside && Object.values(dateElementsTouched).some((e) => e)) {
        triggerBlurAndChange();
      }
    }, [clickedOutside, dateElementsTouched]);

    const handleOnBlur = (
      e: ChangeEvent<HTMLInputElement>,
      element: DateInputs[number],
    ) => {
      setDateElementTouched({ ...dateElementsTouched, ...{ [element]: true } });
      const value = e.target.value;
      const newValue = value.length == 1 ? `0${value}` : value;

      handleChange(newValue, element);
    };

    const handleYearOnBlur = () => {
      setDateElementTouched({ ...dateElementsTouched, ...{ year: true } });
      triggerBlurAndChange();
    };

    useEffect(() => {
      if (dateValue?.year?.length === 4) {
        handleYearOnBlur();
      }
    }, [dateValue.year]);

    return (
      <Flex ref={ref} className={props.className}>
        <ShortInput>
          <StyledTextInput
            {...omit(props, 'className')}
            onChange={(val: string) => handleChange(val, MONTH)}
            onBlur={(e: ChangeEvent<HTMLInputElement>) =>
              handleOnBlur(e, MONTH)
            }
            value={dateValue.month || ''}
            maxLength={2}
            inputMode="numeric"
            placeholder="MM"
            ref={monthInputRef}
            inputWidth={0}
          />
        </ShortInput>
        <ShortInput>
          <StyledTextInput
            {...omit(props, 'className')}
            onChange={(val: string) => handleChange(val, DAY)}
            onBlur={(e: ChangeEvent<HTMLInputElement>) => handleOnBlur(e, DAY)}
            value={dateValue.day || ''}
            maxLength={2}
            inputMode="numeric"
            placeholder="DD"
            ref={dayInputRef}
            inputWidth={0}
          />
        </ShortInput>
        <LongInput>
          <StyledTextInput
            {...omit(props, 'className')}
            onChange={(val: string) => handleChange(val, YEAR)}
            onBlur={handleYearOnBlur}
            value={dateValue.year || ''}
            maxLength={4}
            inputMode="numeric"
            placeholder="YYYY"
            ref={yearInputRef}
            inputWidth={1}
          />
        </LongInput>
      </Flex>
    );
  },
);
